import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from '@/lib/useToast';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextValue {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    resolvedTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Cookie helpers (CLAUDE.md forbids localStorage, cookies are allowed)
function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
}

function setCookie(name: string, value: string, days: number = 365) {
    if (typeof document === 'undefined') return;
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
}

// Get initial theme from cookie (called during lazy state init)
function getInitialTheme(serverTheme: Theme): Theme {
    if (typeof document === 'undefined') return serverTheme;
    const saved = getCookie('septima-theme');
    if (saved && ['light', 'dark', 'system'].includes(saved)) {
        return saved as Theme;
    }
    return serverTheme;
}

interface ThemeProviderProps {
    children: ReactNode;
    /** Initial theme from server-side cookie read */
    initialTheme?: Theme;
}

function ThemeProviderInner({ children, initialTheme = 'system' }: ThemeProviderProps) {
    // Use lazy initialization to read from cookies on client mount
    const [theme, setThemeState] = useState<Theme>(() => getInitialTheme(initialTheme));
    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');
    const { pushToast } = useToast();

    // Wrapper to persist theme to cookie
    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme);
        setCookie('septima-theme', newTheme);
        const label = newTheme.charAt(0).toUpperCase() + newTheme.slice(1);
        pushToast({ kind: 'info', message: `Theme: ${label}` });
    };

    // Handle theme changes
    useEffect(() => {
        const root = document.documentElement;

        const applyTheme = (isDark: boolean) => {
            if (isDark) {
                root.classList.add('dark');
                setResolvedTheme('dark');
            } else {
                root.classList.remove('dark');
                setResolvedTheme('light');
            }
        };

        if (theme === 'system') {
            // Check system preference
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            applyTheme(mediaQuery.matches);

            // Listen for system theme changes
            const handler = (e: MediaQueryListEvent) => applyTheme(e.matches);
            mediaQuery.addEventListener('change', handler);
            return () => mediaQuery.removeEventListener('change', handler);
        } else {
            applyTheme(theme === 'dark');
        }
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function ThemeProvider({ children, initialTheme = 'system' }: ThemeProviderProps) {
    // ThemeProviderInner needs useToast which requires ToastProvider above it in the tree.
    // ThemeProvider is the public API; ThemeProviderInner is the implementation.
    return <ThemeProviderInner initialTheme={initialTheme}>{children}</ThemeProviderInner>;
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
